<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import { dynamicTime } from 'shared/helpers/timeHelper';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
  showConversationInfo: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: null,
  },
  sourceAttribution: {
    type: Object,
    default: null,
  },
  crmSummary: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  ADDRESS: { field: 'address' },
  STATE: { field: 'state' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  address: '',
  state: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    address = '',
    state: stateValue = '',
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    address,
    state: stateValue,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles,
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

// Conversation info computed properties
const conversationAssigneeName = computed(() => {
  if (!props.conversation?.meta?.assignee) return null;
  return props.conversation.meta.assignee.name || props.conversation.meta.assignee.available_name;
});

const conversationAssigneeThumbnail = computed(() => {
  if (!props.conversation?.meta?.assignee) return '';
  return props.conversation.meta.assignee.thumbnail;
});

const conversationStatus = computed(() => {
  const statusMap = {
    open: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.OPEN',
      class: 'bg-green-100 text-green-800',
      icon: 'i-lucide-circle-dot',
    },
    resolved: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.RESOLVED',
      class: 'bg-blue-100 text-blue-800',
      icon: 'i-lucide-check-circle-2',
    },
    pending: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.PENDING',
      class: 'bg-yellow-100 text-yellow-800',
      icon: 'i-lucide-clock',
    },
    snoozed: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.SNOOZED',
      class: 'bg-purple-100 text-purple-800',
      icon: 'i-lucide-moon',
    },
  };
  return statusMap[props.conversation?.status] || null;
});

const conversationCreatedAt = computed(() => {
  if (!props.conversation?.created_at) return '';
  const timestamp = props.conversation.created_at;
  const timestampInSeconds = 
    timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
  return dynamicTime(timestampInSeconds);
});

const conversationLastActivityAt = computed(() => {
  if (!props.conversation?.last_activity_at) return '';
  const timestamp = props.conversation.last_activity_at;
  const timestampInSeconds = 
    timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
  return dynamicTime(timestampInSeconds);
});

// Source attribution computed properties
const originalSource = computed(() => {
  return props.sourceAttribution?.original || null;
});

const lastSource = computed(() => {
  return props.sourceAttribution?.last || null;
});

const hasSourceData = computed(() => {
  return originalSource.value || lastSource.value;
});

const showLastSource = computed(() => {
  if (!lastSource.value || !originalSource.value) return false;
  return lastSource.value.id !== originalSource.value.id;
});

const hasLastSourceData = computed(() => {
  if (!lastSource.value || !originalSource.value) return false;
  return lastSource.value.id !== originalSource.value.id;
});

const formatSourceDate = (timestamp) => {
  if (!timestamp) return t('CONTACT_PANEL.SOURCE.NO_CAPTURE_DATE');
  const timestampInSeconds = 
    timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
  return dynamicTime(timestampInSeconds);
};

const getSourcePlatformName = (platform) => {
  const names = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    whatsapp_direct: 'WhatsApp',
    whatsapp_business: 'WhatsApp Business',
  };
  return names[platform] || 'Campaign';
};

const getSourceDisplay = (source) => {
  if (!source) return { 
    text: t('CONTACT_PANEL.SOURCE.NO_CAPTURE_DATE'), 
    platform: t('CONTACT_PANEL.SOURCE.NO_CAPTURE_DATE'), 
    url: null 
  };
  
  if (source.type === 'organic') {
    return {
      text: t('CONTACT_PANEL.SOURCE.ORGANIC_TRAFFIC'),
      platform: t('CONTACT_PANEL.SOURCE.ORGANIC'),
      url: null,
    };
  }
  
  return {
    text: source.headline || source.url || t('CONTACT_PANEL.SOURCE.PAID_CAMPAIGN'),
    platform: getSourcePlatformName(source.platform),
    url: source.url,
  };
};

// CRM summary computed properties
const hasCrmData = computed(() => {
  return props.crmSummary && props.crmSummary.totalDealsCount > 0;
});

const machinesList = computed(() => {
  if (!props.crmSummary?.machinesPurchased?.length) return '';
  return props.crmSummary.machinesPurchased.join(', ');
});

const formattedRevenue = computed(() => {
  if (!props.crmSummary?.totalRevenueByCurrency?.length) return '';
  
  return props.crmSummary.totalRevenueByCurrency.map(({ currency, amountCents }) => {
    const amount = amountCents / 100;
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency 
    }).format(amount);
  }).join(' + ');
});

const formattedPotentialRevenue = computed(() => {
  if (!props.crmSummary?.potentialRevenueByCurrency?.length) return '';
  
  return props.crmSummary.potentialRevenueByCurrency.map(({ currency, amountCents }) => {
    const amount = amountCents / 100;
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency 
    }).format(amount);
  }).join(' + ');
});

const activeDealsCount = computed(() => {
  return props.crmSummary?.activeDeals?.length || 0;
});

const currentStageName = computed(() => {
  if (!props.crmSummary?.activeDeals?.length) return null;
  // Mostrar el stage del deal más reciente (primero en el array)
  return props.crmSummary.activeDeals[0]?.stageName || null;
});

const currentDealStatus = computed(() => {
  if (!props.crmSummary?.activeDeals?.length) return null;
  const status = props.crmSummary.activeDeals[0]?.status;
  const statusMap = {
    'open': t('CONTACT_PANEL.CRM.STATUS.OPEN'),
    'won': t('CONTACT_PANEL.CRM.STATUS.WON'),
    'lost': t('CONTACT_PANEL.CRM.STATUS.LOST'),
    'canceled': t('CONTACT_PANEL.CRM.STATUS.CANCELED')
  };
  return statusMap[status] || status;
});

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </div>
    <div v-if="showConversationInfo" class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.CONVERSATION_INFO.TITLE') }}
      </span>
      <div v-if="!conversation" class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.CARD.CONVERSATION_INFO.NO_CONVERSATION') }}
      </div>
      <div v-else class="grid grid-cols-2 gap-4 w-full">
        <div v-if="conversationAssigneeName" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CONVERSATION_ASSIGNEE') }}
          </span>
          <div class="flex items-center gap-2">
            <Thumbnail
              :src="conversationAssigneeThumbnail"
              :username="conversationAssigneeName"
              size="24px"
            />
            <span class="text-sm text-n-slate-12">{{ conversationAssigneeName }}</span>
          </div>
        </div>
        <div v-if="conversationStatus" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CONVERSATION_STATUS.LABEL') }}
          </span>
          <div class="inline-flex items-center gap-1.5">
            <span :class="conversationStatus.icon" class="text-base" />
            <span
              class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
              :class="conversationStatus.class"
            >
              {{ t(conversationStatus.label) }}
            </span>
          </div>
        </div>
        <div v-if="conversationCreatedAt" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CONVERSATION_CREATION') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ conversationCreatedAt }}
          </span>
        </div>
        <div v-if="conversationLastActivityAt" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.LAST_ACTIVITY') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ conversationLastActivityAt }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="showConversationInfo" class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOURCE_INFO.TITLE') }}
      </span>
      <div v-if="!hasSourceData" class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.CARD.SOURCE_INFO.NO_SOURCE') }}
      </div>
      <div v-else class="flex flex-col w-full gap-4">
        <!-- Original Source -->
        <div v-if="originalSource" class="flex flex-col gap-2">
          <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
            {{ t('CONTACT_PANEL.SOURCE.ORIGINAL') }}
          </span>
          <div class="grid grid-cols-2 gap-4 w-full">
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CONTACT_PANEL.SOURCE.TITLE') }}
              </span>
              <span class="text-sm text-n-slate-12">
                {{ getSourceDisplay(originalSource).text }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                Plataforma
              </span>
              <span class="text-sm text-n-slate-12">
                {{ getSourceDisplay(originalSource).platform }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                Fecha de captura
              </span>
              <span class="text-sm text-n-slate-12">
                {{ formatSourceDate(originalSource.capturedAt) }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                URL
              </span>
              <a
                v-if="getSourceDisplay(originalSource).url"
                :href="getSourceDisplay(originalSource).url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-blue-10 hover:text-blue-11 hover:underline truncate"
              >
                {{ getSourceDisplay(originalSource).url }}
              </a>
              <span v-else class="text-sm text-n-slate-12">
                {{ t('CONTACT_PANEL.SOURCE.NO_CAPTURE_DATE') }}
              </span>
            </div>
          </div>
        </div>
        <!-- Last Source (if different) -->
        <div v-if="showLastSource" class="flex flex-col gap-2 pt-2 border-t border-n-strong">
          <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
            {{ t('CONTACT_PANEL.SOURCE.LAST') }}
          </span>
          <div class="grid grid-cols-2 gap-4 w-full">
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CONTACT_PANEL.SOURCE.TITLE') }}
              </span>
              <span class="text-sm text-n-slate-12">
                {{ getSourceDisplay(hasLastSourceData ? lastSource : null).text }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                Plataforma
              </span>
              <span class="text-sm text-n-slate-12">
                {{ getSourceDisplay(hasLastSourceData ? lastSource : null).platform }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                Fecha de captura
              </span>
              <span class="text-sm text-n-slate-12">
                {{ hasLastSourceData ? formatSourceDate(lastSource.capturedAt) : t('CONTACT_PANEL.SOURCE.NO_CAPTURE_DATE') }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                URL
              </span>
              <a
                v-if="hasLastSourceData && getSourceDisplay(lastSource).url"
                :href="getSourceDisplay(lastSource).url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-blue-10 hover:text-blue-11 hover:underline truncate"
              >
                {{ getSourceDisplay(lastSource).url }}
              </a>
              <span v-else class="text-sm text-n-slate-12">
                {{ t('CONTACT_PANEL.SOURCE.NO_CAPTURE_DATE') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showConversationInfo" class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.CRM_INFO.TITLE') }}
      </span>
      <div v-if="!hasCrmData" class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.CARD.CRM_INFO.NO_DATA') }}
      </div>
      <div v-else class="grid grid-cols-2 gap-4 w-full">
        <!-- Total de negocios -->
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.TOTAL_DEALS') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ crmSummary.totalDealsCount }}
          </span>
        </div>
        <!-- Negocios abiertos -->
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.ACTIVE_DEALS') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ crmSummary.activeDealsCount }}
          </span>
        </div>
        <!-- Estado del negocio activo -->
        <div v-if="currentDealStatus" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.DEAL_STATUS') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ currentDealStatus }}
          </span>
        </div>
        <!-- Etapa del negocio activo -->
        <div v-if="currentStageName" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.ACTIVE_STAGE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ currentStageName }}
          </span>
        </div>
        <!-- Potencial de ganancia -->
        <div v-if="formattedPotentialRevenue" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.POTENTIAL_REVENUE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ formattedPotentialRevenue }}
          </span>
        </div>
        <!-- Ingresos totales -->
        <div v-if="formattedRevenue" class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.TOTAL_REVENUE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ formattedRevenue }}
          </span>
        </div>
        <!-- Máquinas compradas -->
        <div v-if="machinesList" class="flex flex-col gap-1.5 col-span-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACT_PANEL.CRM.MACHINES') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ machinesList }}
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
